/**
 * Tipos y datos del módulo de verificación (#90). Sin JSX: es lo que se puede
 * probar sin montar nada.
 *
 * Las seis ranuras y por qué son seis
 * -----------------------------------
 * La maqueta del socio traía una séptima sección, roja, con «producto
 * falsificado» y «producto denunciado como robado». **No existe y no puede
 * existir**: es la regla 4 del dominio. Al consumidor nunca se le dice que su
 * producto es ilegítimo — el lenguaje es siempre «no pudimos verificar este
 * código» más el canal de reporte, y la certeza va a la bandeja de alertas de
 * la marca, con la evidencia.
 *
 * Los tres casos colapsan en `unverified`. La única excepción sigue siendo el
 * lote retirado, con su alerta sanitaria, porque ahí el riesgo de no avisar
 * supera al de avisar.
 *
 * Que la lista esté cerrada acá es la primera de tres capas; las otras dos
 * están en el servidor (`routers/verification_settings.py` rechaza al guardar)
 * y en la base (`CHECK` de la migración 0025).
 */
import type { MessageKey } from '@/i18n/messages';

export const LOCALES = ['es', 'en', 'pt'] as const;
export type CopyLocale = (typeof LOCALES)[number];

export const SLOTS = [
  'genuine',
  'already_seen',
  'unverified',
  'recalled',
  'throttled',
  'contact',
] as const;
export type CopySlot = (typeof SLOTS)[number];

/** Estados que la página del consumidor sabe mostrar con `?preview=`. */
export const PREVIEW_STATES = ['genuine', 'already_seen', 'unverified', 'recalled'] as const;
export type PreviewState = (typeof PREVIEW_STATES)[number];

export interface SlotDraft {
  title: string;
  body: string;
}

/** Lo que se está editando: un borrador por idioma y por ranura. */
export type Draft = Record<CopyLocale, Record<CopySlot, SlotDraft>>;

/** Lo que devuelve `GET /api/v1/verification/messages`. */
export interface SlotMessageOut {
  title: string | null;
  body: string | null;
  updated_at: string | null;
  updated_by: number | null;
}

export interface VerificationMessages {
  messages: Partial<Record<CopyLocale, Partial<Record<CopySlot, SlotMessageOut>>>>;
  locales: string[];
  slots: string[];
}

/** Una ranura rechazada por el guardián de vocabulario del servidor. */
export interface Rejection {
  locale: string;
  slot: string;
  field: string;
  terms: string[];
}

/** Topes del servidor (`schemas/verification.py`) y del `CHECK` de la base. */
export const TITLE_MAX = 120;
export const BODY_MAX = 600;

/**
 * Descripción de cada ranura para la interfaz.
 *
 * `preview` dice en qué estado de la página del consumidor se ve el texto.
 * `null` significa que no tiene un estado propio: `contact` aparece en el pie
 * de los estados que ofrecen el canal de reporte, y `throttled` sólo lo ve
 * quien envía varios reportes seguidos. Decirlo en pantalla evita el ticket
 * de "escribí el mensaje y no aparece".
 */
export interface SlotMeta {
  slot: CopySlot;
  labelKey: MessageKey;
  hintKey: MessageKey;
  preview: PreviewState | null;
  /** `false` en las ranuras que sólo tienen cuerpo. */
  hasTitle: boolean;
}

export const SLOT_META: readonly SlotMeta[] = [
  {
    slot: 'genuine',
    labelKey: 'verification.slot.genuine',
    hintKey: 'verification.slot.genuine.hint',
    preview: 'genuine',
    hasTitle: true,
  },
  {
    slot: 'already_seen',
    labelKey: 'verification.slot.already',
    hintKey: 'verification.slot.already.hint',
    preview: 'already_seen',
    hasTitle: true,
  },
  {
    slot: 'unverified',
    labelKey: 'verification.slot.unverified',
    hintKey: 'verification.slot.unverified.hint',
    preview: 'unverified',
    hasTitle: true,
  },
  {
    slot: 'recalled',
    labelKey: 'verification.slot.recalled',
    hintKey: 'verification.slot.recalled.hint',
    preview: 'recalled',
    hasTitle: true,
  },
  {
    slot: 'throttled',
    labelKey: 'verification.slot.throttled',
    hintKey: 'verification.slot.throttled.hint',
    preview: null,
    hasTitle: false,
  },
  {
    slot: 'contact',
    labelKey: 'verification.slot.contact',
    hintKey: 'verification.slot.contact.hint',
    preview: null,
    hasTitle: true,
  },
];

function emptySlots(): Record<CopySlot, SlotDraft> {
  return SLOTS.reduce<Record<CopySlot, SlotDraft>>(
    (acc, slot) => {
      acc[slot] = { title: '', body: '' };
      return acc;
    },
    {} as Record<CopySlot, SlotDraft>,
  );
}

export function emptyDraft(): Draft {
  return LOCALES.reduce<Draft>((acc, locale) => {
    acc[locale] = emptySlots();
    return acc;
  }, {} as Draft);
}

/** Lo guardado, en forma de borrador editable. */
export function draftFrom(saved: VerificationMessages | undefined): Draft {
  const draft = emptyDraft();
  if (!saved) return draft;
  for (const locale of LOCALES) {
    const bloque = saved.messages[locale];
    if (!bloque) continue;
    for (const slot of SLOTS) {
      const entrada = bloque[slot];
      if (!entrada) continue;
      draft[locale][slot] = { title: entrada.title ?? '', body: entrada.body ?? '' };
    }
  }
  return draft;
}

/**
 * El borrador en el formato del `PUT`.
 *
 * Las ranuras vacías **no se envían**: vaciar un campo es volver al texto por
 * defecto del bundle del consumidor, no guardar una cadena en blanco. Con
 * cadenas vacías guardadas, "sin personalizar" dejaría de ser un estado
 * distinguible y la pantalla del consumidor mostraría un título vacío.
 */
export function toPayload(draft: Draft): { messages: Record<string, unknown> } {
  const messages: Record<string, Record<string, { title?: string; body?: string }>> = {};
  for (const locale of LOCALES) {
    const bloque: Record<string, { title?: string; body?: string }> = {};
    for (const slot of SLOTS) {
      const title = draft[locale][slot].title.trim();
      const body = draft[locale][slot].body.trim();
      if (title === '' && body === '') continue;
      bloque[slot] = {
        ...(title === '' ? {} : { title }),
        ...(body === '' ? {} : { body }),
      };
    }
    if (Object.keys(bloque).length > 0) messages[locale] = bloque;
  }
  return { messages };
}

/**
 * El borrador de UN idioma, en el formato que lee la página del consumidor
 * (`sanitizeMessages` en `web/consumer/src/lib/brand.ts`).
 *
 * Sólo el idioma que se está viendo: la vista previa muestra uno a la vez y
 * mandar los tres triplicaría una URL que ya es larga.
 */
export function previewMessages(
  draft: Draft,
  locale: CopyLocale,
): Record<string, Record<string, { title?: string; body?: string }>> {
  const payload = toPayload(draft).messages as Record<
    string,
    Record<string, { title?: string; body?: string }>
  >;
  const bloque = payload[locale];
  return bloque === undefined ? {} : { [locale]: bloque };
}

/**
 * URL de la vista previa: la página del consumidor DE VERDAD, en modo
 * demostración.
 *
 * `?preview=` hace que no consulte la API y use datos de muestra, así que
 * ninguna unidad real participa; `?tenant=` trae el logo y la paleta de la
 * marca; `?xyvmsg=` lleva el borrador sin guardar. La página sólo lee ese
 * último parámetro cuando hay `preview=`, así que no existe forma de armar un
 * enlace que le cambie el texto a alguien que escanea de verdad.
 *
 * Se usa la página real y no una maqueta a propósito: una vista previa que
 * reimplementa la pantalla es una vista previa que miente en cuanto una de
 * las dos cambia.
 */
export function previewUrl(options: {
  base: string;
  state: PreviewState;
  locale: CopyLocale;
  tenantSlug: string;
  draft: Draft;
}): string {
  const params = new URLSearchParams({
    preview: options.state,
    lang: options.locale,
    tenant: options.tenantSlug,
  });
  const mensajes = previewMessages(options.draft, options.locale);
  if (Object.keys(mensajes).length > 0) {
    params.set('xyvmsg', JSON.stringify(mensajes));
  }
  // `/v/<codigo>` y no la raiz: desde que el sitio publico ocupa `/`, la
  // pagina del consumidor solo se alcanza por esa ruta —el borde la reescribe
  // a `verify/index.html`, y el dev server de Astro hace lo mismo hacia la
  // raiz—. Apuntar a `/` mostraria la portada de XYVONA dentro del iframe.
  //
  // El codigo es de relleno y nunca se consulta: `?preview=` corta el camino
  // antes de tocar la API. Es el mismo que muestra la propia pagina cuando no
  // hay ninguno.
  return `${options.base.replace(/\/+$/, '')}/v/PREVIEW00000000DEMO?${params.toString()}`;
}

/** Las violaciones que devolvió el servidor, si el error es del guardián. */
export function rejectionsOf(details: Record<string, unknown> | null): Rejection[] {
  const raw = details?.['rejected'];
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is Rejection => {
    if (typeof item !== 'object' || item === null) return false;
    const row = item as Record<string, unknown>;
    return typeof row['locale'] === 'string' && typeof row['slot'] === 'string';
  });
}

/** ¿Hay algo escrito? Decide si el botón de guardar tiene sentido. */
export function isDirty(draft: Draft, saved: Draft): boolean {
  return JSON.stringify(toPayload(draft)) !== JSON.stringify(toPayload(saved));
}
